import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '@/components/ui/button';
import { TrendingUp, Plus, CalendarClock, CheckCircle2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SalaryRevisionDialog } from './SalaryRevisionDialog';
import { fmtRM } from './payslipEditUtils';

const STATUS_TONE = {
  scheduled: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  applied: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
};

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center"><Icon className="h-5 w-5 text-emerald-600" /></div>
      <div><p className="text-xs text-slate-500 dark:text-slate-400">{label}</p><p className="text-lg font-semibold text-slate-900 dark:text-slate-100" data-testid={testId}>{value}</p></div>
    </div>
  );
}

export function AppraisalTab({ employees, canProcess }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payroll/salary-revisions`);
      setRevisions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load salary revisions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return q ? revisions.filter((r) => `${r.employee_name} ${r.employee_id} ${r.department || ''}`.toLowerCase().includes(q)) : revisions;
  }, [revisions, query]);

  const year = new Date().getFullYear();
  const scheduled = revisions.filter((r) => r.status === 'scheduled').length;
  const appliedThisYear = revisions.filter((r) => r.status === 'applied' && String(r.effective_date || '').startsWith(String(year)));
  const avgPct = appliedThisYear.length ? appliedThisYear.reduce((s, r) => s + Number(r.increment_pct || 0), 0) / appliedThisYear.length : 0;

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="space-y-4" data-testid="appraisal-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={CalendarClock} label="Scheduled revisions" value={scheduled} testId="appraisal-stat-scheduled" />
        <StatCard icon={CheckCircle2} label={`Applied in ${year}`} value={appliedThisYear.length} testId="appraisal-stat-applied" />
        <StatCard icon={TrendingUp} label="Avg increment (applied)" value={`${avgPct.toFixed(2)}%`} testId="appraisal-stat-avg" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search employee…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="appraisal-search" className="pl-9 h-9 dark:bg-slate-900 dark:border-slate-600" />
        </div>
        {canProcess && (
          <Button onClick={() => setDialogOpen(true)} data-testid="new-salary-revision-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="h-4 w-4 mr-2" /> New Salary Revision
          </Button>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700">
              <tr>
                {['Employee', 'Effective', 'Old → New Basic', 'Increment', 'Rating', 'Approved by', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filtered.map((r) => (
                <tr key={r.revision_id} data-testid={`revision-row-${r.revision_id}`} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                  <td className="px-4 py-3 text-sm"><p className="font-medium text-slate-900 dark:text-slate-100">{r.employee_name || 'N/A'}</p><p className="text-xs text-slate-500 font-mono">{r.employee_id}{r.department ? ` · ${r.department}` : ''}</p></td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap text-slate-700 dark:text-slate-300">{r.effective_date}</td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap tabular-nums text-slate-700 dark:text-slate-300">{fmtRM(r.old_basic)} → <span className="font-semibold text-slate-900 dark:text-slate-100">{fmtRM(r.new_basic)}</span></td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap tabular-nums font-medium ${Number(r.increment_amount) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                    {Number(r.increment_amount) >= 0 ? '+' : '−'}{fmtRM(Math.abs(r.increment_amount))}{r.increment_pct != null ? ` (${Number(r.increment_pct).toFixed(2)}%)` : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{r.performance_rating != null ? `${r.performance_rating}/10` : '—'}{r.performance_period ? <span className="block text-xs text-slate-500">{r.performance_period}</span> : null}</td>
                  <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{r.approved_by || '—'}{r.remarks ? <span className="block text-xs text-slate-500 italic max-w-[220px] truncate" title={r.remarks}>{r.remarks}</span> : null}</td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_TONE[r.status] || STATUS_TONE.cancelled}`} data-testid={`revision-status-${r.revision_id}`}>{r.status}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {canProcess && r.status === 'scheduled' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" data-testid={`revision-apply-${r.revision_id}`} className="h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => window.confirm(`Apply ${r.employee_name}'s new basic ${fmtRM(r.new_basic)} now?`) && act(() => axios.post(`${API}/payroll/salary-revisions/${r.revision_id}/apply`), 'Revision applied')}>Apply now</Button>
                        <Button size="sm" variant="outline" data-testid={`revision-cancel-${r.revision_id}`} className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={() => window.confirm('Cancel this scheduled revision?') && act(() => axios.delete(`${API}/payroll/salary-revisions/${r.revision_id}`), 'Revision cancelled')}>Cancel</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12" data-testid="appraisal-empty">
            <TrendingUp className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">{query ? 'No revisions match your search' : 'No salary revisions yet. Record an appraisal outcome to get started.'}</p>
          </div>
        )}
      </div>

      <SalaryRevisionDialog open={dialogOpen} onOpenChange={setDialogOpen} employees={employees} onCreated={load} />
    </div>
  );
}
